import { useCallback, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';

import type { SurveySectionAnswers } from 'bundles/expression-of-interest/queries/CreateOrUpdateSurveySection';
import { useCreateOrUpdateSurveySection } from 'bundles/expression-of-interest/queries/CreateOrUpdateSurveySection';
import type { EoiContext } from 'bundles/expression-of-interest/types';
import {
  clearSavedSurveyResponse,
  readSavedSurveyResponse,
  writeSavedSurveyResponse,
} from 'bundles/expression-of-interest/utils/surveyResponsePersistence';

type Options = {
  productId: string;
  surveyVariant: string;
  eoiContext: EoiContext;
};

type SubmitResult = {
  saved: boolean;
  // Set when the server rejected the section but named the visitor's in-progress response, and
  // this hook had no response id yet.
  existingResponseId?: string;
};

// The server's 400 for a rejected section carries the visitor's in-progress response id in `details`.
const getExistingResponseId = (error: unknown): string | undefined => {
  const body: unknown = (error as { networkError?: { result?: unknown } })?.networkError?.result;
  const details = (body as { details?: { responseId?: unknown } } | null | undefined)?.details;
  return typeof details?.responseId === 'string' ? details.responseId : undefined;
};

/**
 * Submits one survey section per call, reusing the response id from the first call.
 * The id and the next section are also saved in localStorage so a reload resumes at the next section instead of
 * starting a second response.
 */
const useSurveySectionSubmission = ({ productId, surveyVariant, eoiContext }: Options) => {
  const [createOrUpdate] = useCreateOrUpdateSurveySection();

  const [resumed] = useState(() => readSavedSurveyResponse(productId, surveyVariant));
  const responseIdRef = useRef<string | undefined>(resumed?.responseId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitError, setHasSubmitError] = useState(false);

  const submitSection = useCallback(
    async (
      sectionId: string,
      responses: SurveySectionAnswers,
      answers: Record<string, string>
    ): Promise<SubmitResult> => {
      setIsSubmitting(true);
      setHasSubmitError(false);

      try {
        const result = await createOrUpdate({
          variables: {
            productId,
            surveyVariant,
            shouldProcessTerminalActions: true,
            responseId: responseIdRef.current,
            sectionId,
            eoiContext,
            responses,
            trackingFields: { eoiFormVersion: surveyVariant },
          },
        });

        const saved = result.data?.QualificationSurveySectionResponse;
        if (!saved) {
          throw new Error('Survey section response was empty');
        }

        responseIdRef.current = saved.id;
        writeSavedSurveyResponse(productId, {
          responseId: saved.id,
          sectionId: saved.sectionId,
          surveyVariant,
          answers,
        });
        return { saved: true };
      } catch (error) {
        const existingResponseId = responseIdRef.current ? undefined : getExistingResponseId(error);
        if (existingResponseId) {
          return { saved: false, existingResponseId };
        }

        Sentry.captureException(error, { extra: { productId, surveyVariant, sectionId } });
        setHasSubmitError(true);
        return { saved: false };
      } finally {
        setIsSubmitting(false);
      }
    },
    [createOrUpdate, eoiContext, productId, surveyVariant]
  );

  // Continues an in-progress response the server named, from the section the caller says it is on.
  const adoptResponse = useCallback(
    (responseId: string, sectionId: string, answers: Record<string, string>) => {
      responseIdRef.current = responseId;
      writeSavedSurveyResponse(productId, { responseId, sectionId, surveyVariant, answers });
    },
    [productId, surveyVariant]
  );

  const clearSavedResponse = useCallback(() => clearSavedSurveyResponse(productId), [productId]);

  return { submitSection, isSubmitting, hasSubmitError, resumed, adoptResponse, clearSavedResponse };
};

export default useSurveySectionSubmission;
